// Diagnóstico admin de la integración Bókun de PRUEBA.
// Owner/admin only. Nunca devuelve secretos ni PII: solo presencia de
// credenciales, hosts, flags y contadores.

import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/lib/require-auth";
import type { SupabaseClient } from "@supabase/supabase-js";

async function ensureAdmin(userId: string, supabase: SupabaseClient): Promise<void> {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .in("role", ["admin", "owner"])
    .limit(1);
  if (error) throw new Error(error.message);
  if (!data || data.length === 0) throw new Error("No tienes permisos de administrador.");
}

export interface CapabilityRow {
  id: string;
  label: string;
  ready: boolean;
  detail: string;
}

export interface BokunTestIntegrationStatus {
  environment: string;
  host: string;
  allowedHosts: string[];
  liveHostsRejected: string[];
  hostIsLive: boolean;
  requiredTestHost: string;
  flags: {
    writesEnabled: boolean;
    bookingFlowEnabled: boolean;
    v2SchemaConfirmed: boolean;
    bookingSchemaConfirmed: boolean;
    legacyWebhookTokenEnabled: boolean;
  };
  credentials: {
    accessKey: boolean;
    secretKey: boolean;
    appApiKey: boolean;
    appSecret: boolean;
    channelUuid: boolean;
    vendorId: boolean;
  };
  writeGuard: { allowed: boolean; code: string | null; reason: string | null };
  bookingGuard: { allowed: boolean; code: string | null; reason: string | null };
  v2WriteGuard: { allowed: boolean; code: string | null; reason: string | null };
  capabilities: CapabilityRow[];
  queue: {
    tableReady: boolean;
    error: string | null;
    total: number;
    pending: number;
    processed: number;
    deadLetter: number;
    recent: Array<{
      id: string;
      topic: string;
      status: string;
      attempts: number;
      receivedAt: string;
      vendorId: string | null;
      sellerExternalId: string | null;
      error: string | null;
    }>;
  };
  sellerExternalId: {
    configuredInApp: string | null;
    mappedCount: number;
    receivedInRecentEvents: number;
    missingInRecentEvents: number;
    unmappedSellers: string[];
  };
  webhookUrl: string;
  generatedAt: string;
}

export const getBokunTestIntegrationStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<BokunTestIntegrationStatus> => {
    await ensureAdmin(context.userId, context.supabase);

    const { getBokunTestConfig, checkWriteGuard, REQUIRED_TEST_HOST } =
      await import("@/lib/bokun-test/config.server");
    const { listRecentWebhookEvents } = await import("@/lib/bokun-test/webhook-inbox.server");
    const { parseSellerIdMap } = await import("@/lib/bokun-test/seller-external-id");

    const cfg = getBokunTestConfig();
    const write = checkWriteGuard();
    const booking = checkWriteGuard({ requireBookingFlow: true, requireBookingSchema: true });
    const v2Write = checkWriteGuard({ requireV2Schema: true });
    const events = await listRecentWebhookEvents(20);
    const map = parseSellerIdMap(process.env.BOKUN_TEST_SELLER_ID_MAP ?? null);

    const received = events.rows.filter((r) => !!r.seller_external_id_raw);
    const unmapped = [
      ...new Set(
        received
          .map((r) => r.seller_external_id_raw as string)
          .filter((raw) => !Object.prototype.hasOwnProperty.call(map, raw.toLowerCase())),
      ),
    ];

    const capabilities: CapabilityRow[] = [
      {
        id: "v1-read",
        label: "REST v1 · lectura (catálogo productivo)",
        ready: !!process.env.BOKUN_ACCESS_KEY && !!process.env.BOKUN_SECRET_KEY,
        detail: "Credenciales legacy usadas solo para las lecturas ya existentes.",
      },
      {
        id: "v2",
        label: "REST v2 · Experiencias",
        ready: cfg.hasAccessKey && cfg.hasSecretKey,
        detail:
          cfg.writesEnabled && cfg.v2SchemaConfirmed
            ? "Escrituras habilitadas en test (crear/actualizar sin activar)."
            : "Solo lectura y dry-run: faltan BOKUN_TEST_WRITES_ENABLED y/o BOKUN_TEST_V2_SCHEMA_CONFIRMED en true.",
      },
      {
        id: "booking",
        label: "Booking API · reservar/confirmar",
        ready: cfg.hasAccessKey && cfg.hasSecretKey && !!cfg.channelUuid,
        detail:
          cfg.bookingFlowEnabled && cfg.bookingSchemaConfirmed
            ? "Flujo de prueba habilitado (no conectado al checkout público)."
            : "Bloqueado: faltan BOKUN_TEST_BOOKING_FLOW_ENABLED y/o BOKUN_TEST_BOOKING_SCHEMA_CONFIRMED en true.",
      },
      {
        id: "webhook",
        label: "App Webhooks · HMAC-SHA256",
        ready: cfg.hasAppSecret,
        detail: cfg.hasAppSecret
          ? "Verificación oficial activa."
          : "Falta BOKUN_TEST_APP_SECRET: el receptor rechaza todo.",
      },
      {
        id: "queue",
        label: "Cola de eventos",
        ready: events.tableReady,
        detail: events.tableReady
          ? "Tabla disponible; el procesador puede reclamar pendientes."
          : "Migración pendiente: db/pending-migrations/20260918_bokun_webhook_events.sql.",
      },
      {
        id: "seller",
        label: "SellerExternalID",
        ready: !!cfg.sellerExternalId,
        detail: cfg.sellerExternalId
          ? "Configurado en la app; requiere el campo en cada contrato del Marketplace."
          : "Sin BOKUN_TEST_SELLER_EXTERNAL_ID configurado.",
      },
    ];

    return {
      environment: cfg.environment,
      host: cfg.host,
      allowedHosts: cfg.allowedHosts,
      liveHostsRejected: cfg.liveHostsRejected,
      hostIsLive: cfg.hostIsLive,
      requiredTestHost: REQUIRED_TEST_HOST,
      flags: {
        writesEnabled: cfg.writesEnabled,
        bookingFlowEnabled: cfg.bookingFlowEnabled,
        v2SchemaConfirmed: cfg.v2SchemaConfirmed,
        bookingSchemaConfirmed: cfg.bookingSchemaConfirmed,
        legacyWebhookTokenEnabled: cfg.legacyWebhookTokenEnabled,
      },
      credentials: {
        accessKey: cfg.hasAccessKey,
        secretKey: cfg.hasSecretKey,
        appApiKey: cfg.hasAppApiKey,
        appSecret: cfg.hasAppSecret,
        channelUuid: !!cfg.channelUuid,
        vendorId: !!cfg.vendorId,
      },
      writeGuard: write.allowed
        ? { allowed: true, code: null, reason: null }
        : { allowed: false, code: write.code, reason: write.reason },
      bookingGuard: booking.allowed
        ? { allowed: true, code: null, reason: null }
        : { allowed: false, code: booking.code, reason: booking.reason },
      v2WriteGuard: v2Write.allowed
        ? { allowed: true, code: null, reason: null }
        : { allowed: false, code: v2Write.code, reason: v2Write.reason },
      capabilities,
      queue: {
        tableReady: events.tableReady,
        error: events.error,
        total: events.rows.length,
        pending: events.rows.filter((r) => r.status === "pending").length,
        processed: events.rows.filter((r) => r.status === "processed").length,
        deadLetter: events.rows.filter((r) => r.status === "dead_letter").length,
        recent: events.rows.map((r) => ({
          id: r.id,
          topic: r.topic,
          status: r.status,
          attempts: r.attempts,
          receivedAt: r.received_at,
          vendorId: r.vendor_id,
          sellerExternalId: r.seller_external_id,
          error: r.error,
        })),
      },
      sellerExternalId: {
        configuredInApp: cfg.sellerExternalId,
        mappedCount: Object.keys(map).length,
        receivedInRecentEvents: received.length,
        missingInRecentEvents: events.rows.length - received.length,
        unmappedSellers: unmapped,
      },
      webhookUrl: "/api/public/bokun/app-webhook",
      generatedAt: new Date().toISOString(),
    };
  });

// ---------- Fase 1: autenticación · catálogo · disponibilidad ------------

export const runBokunTestReads = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.userId, context.supabase);
    const { runBokunTestReadChecks } = await import("@/lib/bokun-test/read-checks.server");
    return await runBokunTestReadChecks();
  });

// ---------- Dry-run de exportación --------------------------------------

export interface TourOption {
  id: string;
  title: string;
  slug: string;
  active: boolean;
}

export const listCustomToursForExport = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<TourOption[]> => {
    await ensureAdmin(context.userId, context.supabase);
    const { data, error } = await context.supabase
      .from("custom_tours")
      .select("id, title, slug, active")
      .is("deleted_at", null)
      .order("title")
      .limit(100);
    if (error) throw new Error(error.message);
    return (data ?? []) as TourOption[];
  });

export interface DryRunResult {
  tourId: string;
  tourTitle: string;
  componentKeys: string[];
  missingFields: string[];
  warnings: string[];
  valid: boolean;
  errors: string[];
  payloadJson: string;
  /** Recordatorio: el dry-run nunca llama a Bókun. */
  calledBokun: false;
}

export const dryRunExportCustomTour = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { tourId: string }) => {
    if (!data?.tourId || typeof data.tourId !== "string") throw new Error("tourId requerido");
    return data;
  })
  .handler(async ({ data, context }): Promise<DryRunResult> => {
    await ensureAdmin(context.userId, context.supabase);
    const { dryRunExport } = await import("@/lib/bokun-test/experience-mapper");

    const { data: tour, error } = await context.supabase
      .from("custom_tours")
      .select("*")
      .eq("id", data.tourId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!tour) throw new Error("Tour no encontrado.");

    const [pricing, itinerary, questions, meeting, schedules] = await Promise.all([
      context.supabase
        .from("custom_tour_pricing")
        .select("tier, base_price, currency")
        .eq("tour_id", data.tourId),
      context.supabase
        .from("custom_tour_itinerary")
        .select("title, description, duration_minutes, position")
        .eq("tour_id", data.tourId)
        .order("position"),
      context.supabase
        .from("custom_tour_questions")
        .select("label, field_type, required, active")
        .eq("tour_id", data.tourId)
        .order("position"),
      context.supabase
        .from("custom_tour_meeting_points")
        .select("name, kind, address, active")
        .eq("tour_id", data.tourId)
        .order("position"),
      context.supabase.from("custom_tour_schedules").select("*").eq("tour_id", data.tourId),
    ]);

    const prices = (pricing.data ?? []) as Array<{
      tier: string;
      base_price: number;
      currency: string;
    }>;
    const adult = prices.find((p) => p.tier.toLowerCase().includes("adult")) ?? prices[0] ?? null;
    const child = prices.find(
      (p) => p.tier.toLowerCase().includes("child") || p.tier.toLowerCase().includes("nin"),
    );
    const scheduleRows = (schedules.data ?? []) as Array<Record<string, unknown>>;
    const startTimes = [
      ...new Set(
        scheduleRows
          .map((s) => (typeof s.start_time === "string" ? s.start_time : null))
          .filter((v): v is string => !!v),
      ),
    ];
    const weekdays = [
      ...new Set(
        scheduleRows
          .map((s) => (typeof s.weekday === "number" ? s.weekday : null))
          .filter((v): v is number => v !== null),
      ),
    ];
    const pickup = (meeting.data ?? []).some(
      (m) => (m as { kind?: string }).kind?.toLowerCase() === "pickup",
    );
    const firstMeeting = (meeting.data ?? [])[0] as { name?: string; address?: string } | undefined;

    const result = dryRunExport({
      id: tour.id,
      slug: tour.slug,
      title: tour.title,
      summary: tour.short_description,
      description: tour.description,
      duration_minutes: tour.duration_minutes,
      city: tour.city,
      state: tour.state,
      country: tour.country,
      currency: adult?.currency ?? "MXN",
      price_adult: adult?.base_price ?? null,
      price_child: child?.base_price ?? null,
      min_pax: tour.min_participants,
      max_pax: tour.max_participants,
      includes: tour.included,
      excludes: tour.excluded,
      know_before_you_go: tour.recommendations ? [tour.recommendations] : [],
      requirements: tour.requirements ? [tour.requirements] : [],
      itinerary: (itinerary.data ?? []).map((s) => ({
        title: s.title,
        description: s.description,
        duration_minutes: s.duration_minutes,
      })),
      booking_cutoff_hours: tour.booking_cutoff_hours,
      booking_type: startTimes.length > 0 ? "DATE_AND_TIME" : "DATE",
      meeting_point: firstMeeting?.name ?? firstMeeting?.address ?? null,
      pickup_available: pickup,
      pickup_notes: null,
      questions: (questions.data ?? [])
        .filter((q) => q.active !== false)
        .map((q) => ({ label: q.label, type: q.field_type, required: q.required })),
      availability_days: weekdays,
      start_times: startTimes,
      languages: tour.languages,
      external_id: tour.slug,
    });

    return {
      tourId: tour.id,
      tourTitle: tour.title,
      componentKeys: result.componentKeys,
      missingFields: result.missingFields,
      warnings: result.warnings,
      valid: result.validation.valid,
      errors: result.validation.errors,
      payloadJson: result.payloadJson,
      calledBokun: false,
    };
  });

// ---------- Operación del flujo público de reservas (prueba) -------------

export interface BokunTestBookingOps {
  newBookingsEnabled: boolean;
  alerts: Array<{
    id: string;
    code: string;
    reason: string;
    detail: string | null;
    resolvedAt: string | null;
    createdAt: string;
  }>;
  reservations: Array<{
    code: string;
    status: string;
    amount: number | null;
    currency: string | null;
    travelDate: string | null;
    reconciliation: string;
    confirmedAt: string | null;
    paymentRegisteredAt: string | null;
    reviewReason: string | null;
  }>;
}

export const getBokunTestBookingOps = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<BokunTestBookingOps> => {
    await ensureAdmin(context.userId, context.supabase);
    const { newBookingsEnabled } = await import("@/lib/bokun-test/settings.server");
    const { adminClient } = await import("@/lib/bokun-test/payment-pipeline.server");
    const db = adminClient();

    const [{ data: alerts }, { data: reservations }] = await Promise.all([
      db
        .from("bokun_test_review_alerts")
        .select("id, bokun_confirmation_code, reason, detail, resolved_at, created_at")
        .order("created_at", { ascending: false })
        .limit(20),
      db
        .from("bokun_test_reservations")
        .select(
          "bokun_confirmation_code, status, amount, currency, travel_date, reconciliation_status, confirmed_at, payment_registered_at, review_reason",
        )
        .order("reserved_at", { ascending: false })
        .limit(20),
    ]);

    return {
      newBookingsEnabled: await newBookingsEnabled(),
      alerts: (alerts ?? []).map((a) => ({
        id: a.id as string,
        code: a.bokun_confirmation_code as string,
        reason: a.reason as string,
        detail: (a.detail as string | null) ?? null,
        resolvedAt: (a.resolved_at as string | null) ?? null,
        createdAt: a.created_at as string,
      })),
      reservations: (reservations ?? []).map((r) => ({
        code: r.bokun_confirmation_code as string,
        status: r.status as string,
        amount: (r.amount as number | null) ?? null,
        currency: (r.currency as string | null) ?? null,
        travelDate: (r.travel_date as string | null) ?? null,
        reconciliation: (r.reconciliation_status as string) ?? "pending",
        confirmedAt: (r.confirmed_at as string | null) ?? null,
        paymentRegisteredAt: (r.payment_registered_at as string | null) ?? null,
        reviewReason: (r.review_reason as string | null) ?? null,
      })),
    };
  });

/** Interruptor inmediato de reservas NUEVAS (no afecta a las ya creadas). */
export const setBokunTestNewBookings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { enabled: boolean; note?: string }) => ({
    enabled: data?.enabled === true,
    note: typeof data?.note === "string" ? data.note.slice(0, 200) : null,
  }))
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.userId, context.supabase);
    const { setNewBookingsEnabled } = await import("@/lib/bokun-test/settings.server");
    return await setNewBookingsEnabled({
      enabled: data.enabled,
      note: data.note,
      updatedBy: context.userId,
    });
  });
